/**
 * Links compartilháveis do Inbox.
 *
 * O formato `?ticket=<id>[&message=<id>]` já era usado por notificações, push,
 * CRM e relatórios — só que montado à mão em cada lugar. Aqui ele vira fonte
 * única, porque agora o atendente também copia esse link para mandar a um
 * colega, e um param divergente quebraria a leitura em `useTicketDeepLink`.
 *
 * O link é apenas um ponteiro: quem abre continua passando por sessão e pelas
 * validações do backend (`validateTicketOwnership`). Nada de token na URL.
 */

/** Rota do inbox de um cliente. */
function inboxPath(clientId) {
    return `/internal/implementation/${clientId}/inbox`;
}

/**
 * Caminho que abre uma conversa do chat da equipe: o canal ou a mensagem direta e,
 * se houver, a conversa (thread) aberta ao lado. É o par de `useTicketDeepLink`
 * (`?tab=groups&group=<id>[&thread=<id>]`), usado pelo sino e pelo toast.
 *
 * @param {object} params
 * @param {string} params.clientId
 * @param {string} params.groupId
 * @param {string} [params.threadRootId]
 * @returns {string|null} null quando falta o cliente ou a conversa
 */
export function buildGroupInboxPath({ clientId, groupId, threadRootId } = {}) {
    if (!clientId || !groupId) return null;

    const query = new URLSearchParams({ tab: 'groups', group: groupId });
    if (threadRootId) query.set('thread', threadRootId);

    return `${inboxPath(clientId)}?${query.toString()}`;
}

/**
 * Monta a URL absoluta de uma conversa (opcionalmente ancorada numa mensagem).
 *
 * Sem `clientId`, usa o pathname atual — os consumidores (header do chat, barra
 * de ações da mensagem) só existem dentro da própria rota do inbox. Os demais
 * query params da URL atual são descartados de propósito: o link precisa abrir
 * a conversa, não reproduzir o filtro de quem copiou.
 *
 * @param {object} params
 * @param {string} [params.ticketId]
 * @param {string} [params.messageId]
 * @param {string} [params.clientId] força a rota em vez de usar a atual
 * @returns {string|null} null quando não há ticket para apontar
 */
export function buildInboxLink({ ticketId, messageId, clientId } = {}) {
    if (!ticketId) return null;

    const { origin, pathname } = window.location;
    const path = clientId ? inboxPath(clientId) : pathname;

    const query = new URLSearchParams({ ticket: ticketId });
    if (messageId) query.set('message', messageId);

    return `${origin}${path}?${query.toString()}`;
}
